import { logger } from '@adornis/base/logging.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { waitForDialog } from '@adornis/dialog/dialog-functions.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { changePasswordWithToken } from '@adornis/users/db/a-user.js';
import { passwordRegexCheck } from '@adornis/users/db/validators.js';
import { html, nothing } from 'lit';
import { RouterPages } from '../../db/enums/routes.js';

// every rule `passwordRegexCheck` enforces has to be named here — a missing one (the lower case
// letter, once) leaves the user rejected for a reason the form never mentioned
const PASSWORD_RULE_MESSAGE =
  'Das Passwort muss mind. 8 Zeichen lang sein und mind. einen Groß- und einen Kleinbuchstaben, eine Zahl und ein Sonderzeichen enthalten.';

export const SGPasswordReset = stateful((props: { page?: RouterPages.SET_PASSWORT | RouterPages.CHANGE_PASSWORT }) => {
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // `searchParams.get` is reactive and hands back an observable, not the value
  const token = useObservable(() => routingState.searchParams.get('token'), []);
  const isInitialSetup = props.page === RouterPages.SET_PASSWORT;

  // eslint-disable-next-line complexity
  const handlePasswordConfirm = async () => {
    if (isInitialSetup && !privacyAccepted) {
      setErrorMsg('Sie müssen der Datenschutzerklärung zustimmen, wenn Sie fortfahren möchten.');
      return;
    }

    // the empty check has to come first: `passwordValidation` used to run against an empty field
    // and reported the rule violation, hiding the far more obvious "please fill both fields"
    if (!newPassword || !newPasswordRepeat) {
      setErrorMsg('Bitte füllen Sie beide Felder aus.');
      return;
    }

    if (!passwordRegexCheck(newPassword)) {
      setErrorMsg(PASSWORD_RULE_MESSAGE);
      return;
    }

    if (newPassword !== newPasswordRepeat) {
      setErrorMsg('Passwörter stimmen nicht überein');
      return;
    }

    // the mailed token is the only proof of ownership this page has - never change a password without it
    if (!token) {
      setErrorMsg('Ungültige Adresse.');
      return;
    }

    try {
      await changePasswordWithToken(token, newPassword, true)();

      // the session is rebuilt server-side after the change, so hold the user for a moment
      await waitForDialog(new Promise<void>(resolve => setTimeout(resolve, 3000)));
      await subscribeCurrentUser();
      void XSnackbar.show('Erfolgreich geändert ');
      location.href = '/';
    } catch (e) {
      const error = e as Error;
      logger.error(error, 'Error beim Ändern des Passworts');
      setErrorMsg(
        error.message.includes('must contain')
          ? PASSWORD_RULE_MESSAGE
          : 'Dieser Link ist bereits abgelaufen oder wurde bereits verwendet. Bitte beantragen Sie einen neuen Link.',
      );
    }
  };

  const title = isInitialSetup ? 'Passwort anlegen' : 'Passwort zurücksetzen';

  return html`
    <div class="m-auto flex w-full max-w-2xl flex-col gap-4 p-8">
      <h1 class="text-2xl font-bold">${title}</h1>
      <p>${PASSWORD_RULE_MESSAGE}</p>

      ${TextInput({
        value: newPassword,
        type: 'password',
        placeholder: 'Neues Passwort',
        autocomplete: 'new-password',
        onChanged: value => setNewPassword(value ?? ''),
        onPicked: value => setNewPassword(value ?? ''),
      })}
      ${TextInput({
        value: newPasswordRepeat,
        type: 'password',
        placeholder: 'Neues Passwort bestätigen',
        autocomplete: 'new-password',
        onChanged: value => setNewPasswordRepeat(value ?? ''),
        onPicked: value => setNewPasswordRepeat(value ?? ''),
        onSubmit: () => void handlePasswordConfirm(),
      })}
      ${errorMsg ? html`<p class="text-error">${errorMsg}</p>` : nothing}
      ${
        isInitialSetup
          ? CheckboxInput({
              value: privacyAccepted,
              onPicked: value => setPrivacyAccepted(!!value),
              label: html`
                <span>
                  Hiermit bestätige ich, dass ich die
                  <a class="link" href=${`/${RouterPages.DATENSCHUTZ}`} target="_blank">Datenschutzerklärung</a>
                  gelesen habe und dieser zustimme.
                </span>
              `,
            })
          : nothing
      }

      <button class="btn btn-primary" @click=${handlePasswordConfirm}>Passwort zurücksetzen</button>
    </div>
  `;
});
